import React, { createContext, useContext, useState } from 'react';
import { cn } from '../../lib/cn';

type DropdownMenuContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenuContext() {
  const ctx = useContext(DropdownMenuContext);
  if (!ctx) throw new Error('DropdownMenu components must be used within <DropdownMenu>');
  return ctx;
}

export function DropdownMenu({
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internal, setInternal] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internal;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternal(next);
    onOpenChange?.(next);
  };

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      <div data-slot="dropdown-menu" className="relative inline-block">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

export function DropdownMenuTrigger({
  children,
  asChild,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  const { open, setOpen } = useDropdownMenuContext();
  const toggle = () => setOpen(!open);

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<Record<string, unknown>>;
    return React.cloneElement(child, {
      ...props,
      'data-slot': 'dropdown-menu-trigger',
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      onClick: (event: React.MouseEvent) => {
        (child.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(event);
        toggle();
      },
    });
  }

  return (
    <button
      type="button"
      data-slot="dropdown-menu-trigger"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={toggle}
      {...props}
    >
      {children}
    </button>
  );
}

export function DropdownMenuPortal({ children }: { children: React.ReactNode }) {
  // No real portal needed for this overlay model; render inline.
  return <>{children}</>;
}

export function DropdownMenuContent({
  className,
  children,
  align = 'start',
  sideOffset = 4,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}) {
  const { open, setOpen } = useDropdownMenuContext();
  if (!open) return null;

  const alignClass =
    align === 'end' ? 'right-0' : align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0';

  return (
    <>
      {/* Click-outside scrim */}
      <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
      <div
        role="menu"
        data-slot="dropdown-menu-content"
        data-state="open"
        className={cn(
          'absolute z-50 max-h-[var(--radix-dropdown-menu-content-available-height,24rem)] min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 text-[var(--ink-1)] shadow-[var(--sh-3)]',
          alignClass,
          className,
        )}
        style={{ top: `calc(100% + ${sideOffset}px)` }}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

export function DropdownMenuGroup({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div role="group" data-slot="dropdown-menu-group" className={className} {...props} />;
}

export function DropdownMenuItem({
  className,
  inset,
  variant = 'default',
  onSelect,
  onClick,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  onSelect?: (event: { preventDefault: () => void }) => void;
}) {
  const { setOpen } = useDropdownMenuContext();
  const disabled = (props as { disabled?: boolean }).disabled;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClick?.(event);
    let prevented = false;
    onSelect?.({ preventDefault: () => (prevented = true) });
    if (!prevented) setOpen(false);
  };

  return (
    <div
      role="menuitem"
      data-slot="dropdown-menu-item"
      data-inset={inset ? '' : undefined}
      data-variant={variant}
      data-disabled={disabled ? '' : undefined}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }}
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-[var(--r-sm)] px-2 py-1.5 text-sm outline-none transition-colors focus:bg-[var(--surface-mute)] focus:text-[var(--ink-1)] hover:bg-[var(--surface-mute)] hover:text-[var(--ink-1)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-[var(--terracotta)] data-[variant=destructive]:hover:bg-[color-mix(in_srgb,var(--terracotta)_10%,transparent)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-[var(--ink-3)]",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  onSelect,
  onClick,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  checked?: boolean;
  disabled?: boolean;
  onSelect?: (event: { preventDefault: () => void }) => void;
}) {
  const { setOpen } = useDropdownMenuContext();
  return (
    <div
      role="menuitemcheckbox"
      aria-checked={checked}
      data-slot="dropdown-menu-checkbox-item"
      data-state={checked ? 'checked' : 'unchecked'}
      tabIndex={0}
      onClick={(event) => {
        onClick?.(event);
        let prevented = false;
        onSelect?.({ preventDefault: () => (prevented = true) });
        if (!prevented) setOpen(false);
      }}
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-[var(--r-sm)] py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-[var(--surface-mute)] focus:text-[var(--ink-1)] hover:bg-[var(--surface-mute)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        {checked && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="size-3.5" aria-hidden>
            <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}

export function DropdownMenuRadioGroup({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div role="radiogroup" data-slot="dropdown-menu-radio-group" className={className} {...props} />;
}

export function DropdownMenuRadioItem({
  className,
  children,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { value?: string; disabled?: boolean }) {
  const { setOpen } = useDropdownMenuContext();
  return (
    <div
      role="menuitemradio"
      data-slot="dropdown-menu-radio-item"
      tabIndex={0}
      onClick={(event) => {
        onClick?.(event);
        setOpen(false);
      }}
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-[var(--r-sm)] py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-[var(--surface-mute)] focus:text-[var(--ink-1)] hover:bg-[var(--surface-mute)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <svg viewBox="0 0 24 24" fill="currentColor" className="size-2" aria-hidden>
          <circle cx="12" cy="12" r="12" />
        </svg>
      </span>
      {children}
    </div>
  );
}

export function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }) {
  return (
    <div
      data-slot="dropdown-menu-label"
      data-inset={inset ? '' : undefined}
      className={cn('px-2 py-1.5 text-sm font-medium text-[var(--ink-1)] data-[inset]:pl-8', className)}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="separator"
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-[var(--cream-2)]', className)}
      {...props}
    />
  );
}

export function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn('ml-auto text-xs tracking-widest text-[var(--ink-3)]', className)}
      {...props}
    />
  );
}

export function DropdownMenuSub({ children }: { children: React.ReactNode }) {
  return <div data-slot="dropdown-menu-sub">{children}</div>;
}

export function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }) {
  return (
    <div
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset ? '' : undefined}
      className={cn(
        'flex cursor-default select-none items-center rounded-[var(--r-sm)] px-2 py-1.5 text-sm outline-none focus:bg-[var(--surface-mute)] focus:text-[var(--ink-1)] data-[inset]:pl-8',
        className,
      )}
      {...props}
    >
      {children}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="ml-auto size-4" aria-hidden>
        <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function DropdownMenuSubContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dropdown-menu-sub-content"
      className={cn(
        'z-50 min-w-[8rem] overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 text-[var(--ink-1)] shadow-[var(--sh-3)]',
        className,
      )}
      {...props}
    />
  );
}
